import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, ExternalLink, Pencil, Trash2, Presentation, X, Link2, Search } from "lucide-react";
import { toast } from "sonner";
import { API } from "./SharedComponents";

const PresentationsManager = () => {
  const [presentations, setPresentations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState({ title: "", description: "", url: "", category: "" });

  useEffect(() => { fetchPresentations(); }, []);

  const fetchPresentations = async () => {
    try {
      const res = await fetch(`${API}/coach/presentations`, { credentials: "include" });
      if (res.ok) setPresentations(await res.json());
    } catch { toast.error("Failed to load presentations"); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.url.trim()) return toast.error("Title and URL are required");
    try {
      const method = editing ? "PUT" : "POST";
      const url = editing ? `${API}/coach/presentations/${editing}` : `${API}/coach/presentations`;
      const res = await fetch(url, {
        method, credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        toast.success(editing ? "Presentation updated" : "Presentation added");
        resetForm();
        fetchPresentations();
      } else toast.error("Failed to save");
    } catch { toast.error("Something went wrong"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this presentation?")) return;
    try {
      const res = await fetch(`${API}/coach/presentations/${id}`, { method: "DELETE", credentials: "include" });
      if (res.ok) { toast.success("Deleted"); fetchPresentations(); }
    } catch { toast.error("Failed to delete"); }
  };

  const openEdit = (p) => {
    setEditing(p.presentation_id);
    setForm({ title: p.title, description: p.description || "", url: p.url, category: p.category || "" });
    setShowForm(true);
  };

  const resetForm = () => {
    setShowForm(false);
    setEditing(null);
    setForm({ title: "", description: "", url: "", category: "" });
  };

  const filtered = presentations.filter(p =>
    !search || p.title.toLowerCase().includes(search.toLowerCase()) ||
    (p.category || "").toLowerCase().includes(search.toLowerCase())
  );

  const categories = [...new Set(presentations.map(p => p.category).filter(Boolean))];

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="presentations-manager">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white">Presentations</h2>
          <p className="text-sm text-gray-400 mt-1">{presentations.length} presentation{presentations.length !== 1 ? "s" : ""} saved</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowForm(true); }}
          className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-500 text-white px-4 py-2.5 rounded-xl font-medium hover:from-blue-500 hover:to-blue-400 transition-all shadow-lg shadow-blue-500/25"
          data-testid="add-presentation-btn"
        >
          <Plus className="w-4 h-4" /> Add Presentation
        </button>
      </div>

      {/* Search */}
      {presentations.length > 0 && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search presentations..."
            className="w-full pl-10 pr-4 py-2.5 bg-[#141B2D] border border-[#1E293B] rounded-xl text-white text-sm focus:border-blue-500 outline-none"
            data-testid="presentations-search"
          />
        </div>
      )}

      {/* Category Pills */}
      {categories.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setSearch(search === cat ? "" : cat)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                search === cat ? "bg-blue-500 text-white" : "bg-[#1E293B] text-gray-400 hover:bg-[#2D3B4F]"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      )}

      {/* Presentations Grid */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 bg-[#141B2D] border border-[#1E293B] rounded-2xl">
          <Presentation className="w-12 h-12 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400 font-medium">{search ? "No matching presentations" : "No presentations yet"}</p>
          <p className="text-gray-600 text-sm mt-1">{search ? "Try a different search" : "Add your first presentation link"}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((p) => (
            <motion.div
              key={p.presentation_id}
              layout
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-[#141B2D] border border-[#1E293B] rounded-2xl p-5 hover:border-blue-500/30 transition-all group"
              data-testid={`presentation-card-${p.presentation_id}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2.5">
                  <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
                    <Presentation className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white text-sm leading-tight">{p.title}</h3>
                    {p.category && <span className="text-[10px] text-blue-400 font-medium uppercase tracking-wider">{p.category}</span>}
                  </div>
                </div>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => openEdit(p)} className="p-1.5 rounded-lg hover:bg-[#1E293B] text-gray-500 hover:text-white transition-colors" data-testid={`edit-pres-${p.presentation_id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => handleDelete(p.presentation_id)} className="p-1.5 rounded-lg hover:bg-red-500/10 text-gray-500 hover:text-red-400 transition-colors" data-testid={`delete-pres-${p.presentation_id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              {p.description && <p className="text-xs text-gray-400 mb-3 line-clamp-2">{p.description}</p>}
              <a
                href={p.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-xs text-blue-400 hover:text-blue-300 transition-colors"
                data-testid={`open-pres-${p.presentation_id}`}
              >
                <ExternalLink className="w-3 h-3" /> Open Presentation
              </a>
            </motion.div>
          ))}
        </div>
      )}

      {/* Add/Edit Form Modal */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
            onClick={resetForm}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-[#0F172A] border border-[#1E293B] rounded-2xl p-6 w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
              data-testid="presentation-form-modal"
            >
              <div className="flex justify-between items-center mb-5">
                <h3 className="text-lg font-bold text-white">{editing ? "Edit Presentation" : "Add Presentation"}</h3>
                <button onClick={resetForm} className="p-1 text-gray-500 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1.5">Title *</label>
                  <input
                    type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                    placeholder="e.g., Speed Development Lecture"
                    className="w-full px-3 py-2.5 bg-[#0B0F1A] border border-[#1E293B] rounded-xl text-white text-sm focus:border-blue-500 outline-none"
                    data-testid="pres-form-title"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1.5">URL *</label>
                  <div className="relative">
                    <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <input
                      type="url" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })}
                      placeholder="https://..."
                      className="w-full pl-10 pr-3 py-2.5 bg-[#0B0F1A] border border-[#1E293B] rounded-xl text-white text-sm focus:border-blue-500 outline-none"
                      data-testid="pres-form-url"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1.5">Category</label>
                  <input
                    type="text" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                    placeholder="e.g., Speed, Strength, Nutrition"
                    className="w-full px-3 py-2.5 bg-[#0B0F1A] border border-[#1E293B] rounded-xl text-white text-sm focus:border-blue-500 outline-none"
                    data-testid="pres-form-category"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1.5">Description</label>
                  <textarea
                    value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                    rows={3} placeholder="Brief notes about this presentation..."
                    className="w-full px-3 py-2.5 bg-[#0B0F1A] border border-[#1E293B] rounded-xl text-white text-sm focus:border-blue-500 outline-none resize-none"
                    data-testid="pres-form-description"
                  />
                </div>
                <div className="flex gap-3 pt-2">
                  <button onClick={resetForm} className="flex-1 px-4 py-2.5 bg-[#1E293B] text-gray-300 rounded-xl font-medium hover:bg-[#2D3B4F] transition-colors">
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    className="flex-1 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-xl font-medium hover:from-blue-500 hover:to-blue-400 transition-all shadow-lg shadow-blue-500/25"
                    data-testid="pres-form-save"
                  >
                    {editing ? "Update" : "Add"}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PresentationsManager;
